import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowRight, ChevronDown, ChevronRight, Zap, Trash2, Pencil, LayoutTemplate, CalendarDays, Search, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuthStore } from "../store/authStore";
import { useViewScopeStore } from "../store/viewScopeStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import TrashBin from "../components/projects/TrashBin";
import QuestionWizard, { buildAnswersPayload } from "../components/expo/QuestionWizard";

export default function Projects({ mode }) {
  const isFair = mode === "fair";
  const { t } = useTranslation();
  const { user, tenant, fetchMe } = useAuthStore();
  const { scope } = useViewScopeStore();
  const isMe = scope === "me";
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#0055FF", todo_template: "", fair_id: "" });
  // ==== Proje To Do List sihirbazı (EXPO) ====
  const [wizardMode, setWizardMode] = useState(false);
  const [wizQuestions, setWizQuestions] = useState([]);
  const [wizAnswers, setWizAnswers] = useState({});
  const [listKind, setListKind] = useState("project"); // "project" | "general"
  const [memberIds, setMemberIds] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [fairs, setFairs] = useState([]);
  const [expandedCards, setExpandedCards] = useState(() => new Set());
  const [projectLists, setProjectLists] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name} or null
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [trashRefreshKey, setTrashRefreshKey] = useState(0);

  const isSuperAdmin =
    user?.role === "super_admin" || user?.role === "ultra_admin";

  const [editTarget, setEditTarget] = useState(null); // {id} or null
  const [editForm, setEditForm] = useState({ name: "", description: "", todo_template: "", fair_id: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editInitialTemplate, setEditInitialTemplate] = useState("");
  const [editInitialFair, setEditInitialFair] = useState("");

  const requestEdit = (project) => {
    setEditTarget({ id: project.id, kind: project.kind || "project" });
    setEditForm({
      name: project.name || "",
      description: project.description || "",
      todo_template: project.todo_template || "",
      fair_id: project.fair_id || "",
    });
    setEditInitialTemplate(project.todo_template || "");
    setEditInitialFair(project.fair_id || "");
  };

  const confirmEdit = async () => {
    if (!editTarget || !editForm.name.trim()) return;
    setSavingEdit(true);
    try {
      await api.patch(`/projects/${editTarget.id}`, {
        name: editForm.name.trim(),
        description: editForm.description,
        ...(hasExpo && editForm.todo_template !== editInitialTemplate
          ? { todo_template: editForm.todo_template }
          : {}),
        ...(hasExpo && editForm.fair_id !== editInitialFair
          ? { fair_id: editForm.fair_id }
          : {}),
      });
      toast.success("Proje güncellendi");
      setEditTarget(null);
      window.dispatchEvent(new Event("serpix:projects-changed"));
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Proje güncellenemedi",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const requestDelete = (project) => {
    setDeleteTarget({ id: project.id, name: project.name });
    setDeleteConfirmName("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      // Rich Sonner toast with an "Undo" action — one click restores
      // the project (same effect as the Trash panel button). Falls back
      // silently if the restore endpoint rejects (e.g. race with purge).
      const restoreId = deleteTarget.id;
      const restoreName = deleteTarget.name;
      toast.success(
        t("trash.trashed_toast", { name: restoreName }),
        {
          duration: 8000,
          action: {
            label: t("trash.undo"),
            onClick: async () => {
              try {
                await api.post(`/projects/${restoreId}/restore`);
                toast.success(t("trash.restore_success", { name: restoreName }));
                window.dispatchEvent(new Event("serpix:projects-changed"));
                setTrashRefreshKey((k) => k + 1);
                load();
              } catch {
                toast.error(t("trash.restore_error"));
              }
            },
          },
        },
      );
      setDeleteTarget(null);
      setDeleteConfirmName("");
      // Notify sidebar / other pages that the projects list changed.
      window.dispatchEvent(new Event("serpix:projects-changed"));
      // Refresh trash panel + main list
      setTrashRefreshKey((k) => k + 1);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Proje silinemedi",
      );
    } finally {
      setDeleting(false);
    }
  };

  // Read a Dashboard drill-down filter (`#filter=all|open|overdue|done_this_week`)
  // out of the URL. When present we auto-expand every project card so the
  // user sees underlying tasks without a second click. Purely a UX shortcut —
  // the actual task rows still render whatever the ProjectCard shows.
  const filterMatch = (location.hash || "").match(/filter=([a-z_]+)/i);
  const filterKey = filterMatch ? filterMatch[1] : null;
  const FILTER_LABELS = {
    all: "Tüm görevler",
    open: "Açık görevler",
    overdue: "Gecikmiş görevler",
    done_this_week: "Bu hafta tamamlananlar",
  };

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(
      (data || []).filter((p) => (isFair ? p.kind === "fair" : p.kind !== "fair")),
    );
  };

  // Always refresh the user's own record when landing here — picks up
  // toggles set by the super admin (e.g. `can_create_projects`) without
  // requiring the user to log out and back in.
  useEffect(() => {
    fetchMe?.();
    load();
    const onFocus = () => fetchMe?.();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchMe]);

  // When landing here from a Dashboard KPI card, expand every project so
  // the user immediately sees all task rows underneath. Only fires when
  // the filter hash flips (not on every projects reload).
  useEffect(() => {
    if (!filterKey || projects.length === 0) return;
    setExpandedCards(new Set(projects.map((p) => p.id)));
    projects.forEach((p) => loadListsFor(p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, projects.length]);

  const loadListsFor = async (projectId) => {
    if (projectLists[projectId]) return;
    try {
      const { data } = await api.get(`/lists?project_id=${projectId}`);
      setProjectLists((m) => ({ ...m, [projectId]: data || [] }));
    } catch {
      setProjectLists((m) => ({ ...m, [projectId]: [] }));
    }
  };

  const toggleCard = (projectId) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
        loadListsFor(projectId);
      }
      return next;
    });
  };

  const create = async (e) => {
    e.preventDefault();
    if (memberIds.length > 1 && !teamName.trim()) {
      toast.error("Birden fazla kişi seçtiniz — lütfen bir takım adı girin");
      return;
    }
    const isGeneral = listKind === "general";
    if (isFair && !form.fair_id) {
      toast.error("Lütfen bir fuar seçin — şablon ve tarihler fuara göre oluşturulur");
      return;
    }
    try {
      const { data: proj } = await api.post("/projects", {
        name: form.name,
        description: form.description,
        color: form.color,
        kind: listKind,
        member_ids: memberIds,
        team_name: memberIds.length > 1 ? teamName.trim() : null,
        todo_template: isFair ? "auto" : !isGeneral ? form.todo_template || null : null,
        fair_id: !isGeneral ? form.fair_id || null : null,
      });
      if (!isGeneral && wizardMode) {
        const payload = buildAnswersPayload(wizQuestions, wizAnswers);
        if (payload.length > 0 && proj?.id) {
          await api.post(`/projects/${proj.id}/expo-todo-list`, { answers: payload });
          toast.success(isFair ? "Fuar To Do List oluşturuldu" : "Proje To Do List oluşturuldu");
        }
      } else if (!isGeneral && hasExpo && memberIds[0] && proj?.id) {
        try {
          await api.post(`/projects/${proj.id}/expo-questionnaire`, { owner_user_id: memberIds[0] });
          toast.success("Soru formu sorumluya e-posta ile gönderildi");
        } catch {
          toast.error("Soru formu e-postası gönderilemedi");
        }
      }
      setOpen(false);
      setForm({ name: "", description: "", color: "#0055FF", todo_template: "", fair_id: "" });
      setWizardMode(false);
      setWizAnswers({});
      setMemberIds([]);
      setTeamName("");
      setMemberQuery("");
      toast.success(
        isFair
          ? "Fuar To Do List oluşturuldu"
          : isGeneral
            ? "Genel To Do List oluşturuldu"
            : "Proje oluşturuldu",
      );
      // Notify sidebar / other pages so the new project appears immediately.
      window.dispatchEvent(new Event("serpix:projects-changed"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openWizard = async () => {
    if (!form.name.trim()) {
      toast.error("Önce proje adını girin");
      return;
    }
    try {
      if (wizQuestions.length === 0) {
        const { data } = await api.get("/expo/questions");
        setWizQuestions(data.questions || []);
      }
      setWizardMode(true);
    } catch {
      toast.error("Sorular yüklenemedi");
    }
  };

  const canCreate = ["super_admin", "ultra_admin"].includes(user?.role) || !!user?.can_create_projects;
  const hasExpo = Array.isArray(tenant?.enabled_features)
    ? tenant.enabled_features.includes("expo")
    : false;
  const TODO_TEMPLATES = [
    { value: "usa", label: "USA To Do List" },
    { value: "europe", label: "EUROPE To Do List" },
    { value: "turkiye", label: "Türkiye To Do List" },
    { value: "other", label: "Diğer To Do List" },
  ];
  const templateLabel = (key) =>
    (TODO_TEMPLATES.find((tpl) => tpl.value === key) || {}).label || key;
  const fairById = Object.fromEntries(fairs.map((f) => [f.id, f]));

  useEffect(() => {
    api
      .get("/users")
      .then(({ data }) => setMembers((data || []).filter((u) => u.active !== false)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasExpo) return;
    api
      .get("/expo/fairs")
      .then(({ data }) => setFairs(data))
      .catch(() => {});
  }, [hasExpo]);

  const FairSelect = ({ value, onChange, testId, required = false }) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">
        Fuar{required ? " *" : ""}
      </Label>
      <Select
        value={value || "__none__"}
        onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
      >
        <SelectTrigger className="mt-1" data-testid={testId}>
          <SelectValue placeholder={required ? "Fuar seçin…" : "Fuarsız"} />
        </SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value="__none__">— Fuarsız —</SelectItem>}
          {fairs.map((f) => (
            <SelectItem key={f.id} value={f.id}>
              {f.name}
              {f.country ? ` · ${f.country}` : ""}
              {f.start_date ? ` (${f.start_date})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-1 text-[11px] text-zinc-400">
        {required
          ? "To Do List şablonu fuarın ülkesine göre otomatik seçilir; görev tarihleri fuar tarihlerine göre hesaplanır."
          : "Fuar seçilirse şablon görevlerinin son tarihleri fuar tarihine göre otomatik atanır."}
      </p>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Dashboard drill-down banner — shows which KPI card led us here
          plus a shortcut to return home. Rendered only when a filter
          hash is actually present, so casual visits stay quiet. */}
      {filterKey && (
        <div
          data-testid="projects-filter-banner"
          className="flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50/70 px-4 py-2.5 text-sm"
        >
          <div className="flex items-center gap-2 text-violet-900">
            <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              Filtre
            </span>
            <span className="font-medium">
              {FILTER_LABELS[filterKey] || filterKey}
            </span>
            <span className="text-xs text-violet-700/70">
              — tüm projeler otomatik olarak açıldı
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/app/projects", { replace: true })}
            className="rounded-md px-2 py-1 text-xs font-medium text-violet-800 hover:bg-violet-100"
            data-testid="projects-filter-clear"
          >
            Filtreyi kaldır ✕
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t("brand")}
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {isFair ? "Fuarlar" : t("projects.title")}
          </h1>
        </div>
        {canCreate && (
          <>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {isFair ? (
              <Button
                data-testid="new-fair-todo-btn"
                onClick={() => {
                  setListKind("fair");
                  setOpen(true);
                }}
                className="w-full gap-2 bg-violet-600 text-white hover:bg-violet-700 sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                Fuar To Do List
              </Button>
            ) : (
              <Button
                data-testid="new-general-todo-btn"
                onClick={() => {
                  setListKind("general");
                  setOpen(true);
                }}
                variant="outline"
                className="w-full gap-2 border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                Yeni To Do List
              </Button>
            )}
          </div>
          <Dialog
            open={open}
            onOpenChange={(o) => {
              setOpen(o);
              if (!o) {
                setWizardMode(false);
                setWizAnswers({});
              }
            }}
          >
            <DialogContent className={wizardMode ? "max-w-lg" : "max-w-md"}>
              <DialogHeader>
                <DialogTitle>
                  {wizardMode
                    ? `${form.name} — ${isFair ? "Fuar" : "Proje"} To Do List`
                    : isFair
                      ? "Fuar To Do List"
                      : listKind === "general"
                        ? "Yeni To Do List"
                        : t("projects.new")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className={wizardMode ? "hidden" : "space-y-4"}>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    {listKind === "general" ? "Liste Adı" : t("projects.name")}
                  </Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid={TEST_IDS.projectNameInput}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    {t("projects.description")}
                  </Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid={TEST_IDS.projectDescInput}
                    className="mt-1"
                  />
                </div>
                {listKind === "project" && hasExpo && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      To Do List Şablonu
                    </Label>
                    <Select
                      value={form.todo_template || "__none__"}
                      onValueChange={(v) =>
                        setForm({ ...form, todo_template: v === "__none__" ? "" : v })
                      }
                    >
                      <SelectTrigger className="mt-1" data-testid="project-template-select">
                        <SelectValue placeholder="Şablonsuz" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Şablonsuz</SelectItem>
                        {TODO_TEMPLATES.map((tpl) => (
                          <SelectItem key={tpl.value} value={tpl.value} data-testid={`project-template-${tpl.value}`}>
                            {tpl.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] text-zinc-400">
                      Şablon seçilirse proje grupları ve görev taslakları otomatik oluşturulur.
                    </p>
                  </div>
                )}
                {listKind !== "general" && hasExpo && (
                  <FairSelect
                    value={form.fair_id}
                    onChange={(v) => setForm({ ...form, fair_id: v })}
                    testId="project-fair-select"
                    required={isFair}
                  />
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    Kişiler (Takım Çalışması)
                  </Label>
                  <Input
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    placeholder="Kişi ara…"
                    className="mt-1 h-8 text-xs"
                    data-testid="project-member-search"
                  />
                  <div
                    className="mt-1 max-h-36 overflow-y-auto rounded-md border border-zinc-200 p-1"
                    data-testid="project-member-list"
                  >
                    {members
                      .filter((m) =>
                        (m.name || "")
                          .toLocaleLowerCase("tr")
                          .includes(memberQuery.toLocaleLowerCase("tr")),
                      )
                      .sort((a, b) =>
                        (a.name || "").localeCompare(b.name || "", "tr"),
                      )
                      .map((m) => (
                        <label
                          key={m.id}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50"
                          data-testid={`project-member-option-${m.id}`}
                        >
                          <Checkbox
                            checked={memberIds.includes(m.id)}
                            onCheckedChange={(v) =>
                              setMemberIds((cur) =>
                                v ? [...cur, m.id] : cur.filter((x) => x !== m.id),
                              )
                            }
                          />
                          <span className="truncate">{m.name}</span>
                        </label>
                      ))}
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-400">
                    {listKind === "project" && hasExpo
                      ? "Birden fazla kişi seçebilirsiniz. İlk seçilen kişi sorumludur; soruları burada yanıtlamazsanız form linki kendisine e-posta ile gönderilir."
                      : "Birden fazla kişi seçebilirsiniz — seçilenler bu listeye erişir."}
                  </p>
                </div>
                {memberIds.length > 1 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">
                      Takım Adı *
                    </Label>
                    <Input
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="örn. Tasarım Ekibi"
                      className="mt-1"
                      data-testid="project-team-name-input"
                    />
                    <p className="mt-1 text-[11px] text-zinc-400">
                      Birden fazla kişi seçtiniz — takım, sol menüdeki
                      &quot;Takımlar&quot; bölümünde bu listeyle birlikte görünecek.
                    </p>
                  </div>
                )}
                </div>
                {wizardMode && (
                  <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-zinc-200 bg-zinc-50/60 p-4">
                    <QuestionWizard
                      questions={wizQuestions}
                      answers={wizAnswers}
                      onChange={setWizAnswers}
                    />
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {wizardMode ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setWizardMode(false)}
                        data-testid="project-wizard-back-btn"
                      >
                        Geri
                      </Button>
                      <Button
                        type="submit"
                        data-testid="project-wizard-create-btn"
                        className="bg-violet-600 text-white hover:bg-violet-700"
                      >
                        Oluştur + To Do List
                      </Button>
                    </>
                  ) : (
                    <>
                      {listKind !== "general" && hasExpo && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={openWizard}
                          data-testid="project-todo-wizard-btn"
                          className="mr-auto border-violet-300 text-violet-700 hover:bg-violet-50"
                        >
                          {isFair ? "FUAR TO DO LIST" : "PROJE TO DO LIST"}
                        </Button>
                      )}
                      <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                        {t("projects.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        data-testid={TEST_IDS.projectSaveBtn}
                        className="bg-zinc-900 text-white hover:bg-zinc-800"
                      >
                        {t("projects.save")}
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </>
        )}
      </div>

      {projects.length > 0 && (
        <div className="relative max-w-md" data-testid="projects-search-box">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isFair ? "Fuar projesi ara... (ad, açıklama, fuar)" : "Proje ara... (ad, açıklama, şablon, fuar)"}
            data-testid="projects-search-input"
            className="pl-9 pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="projects-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-16 text-center">
          <p className="text-sm text-zinc-500">
            {isFair
              ? "Henüz fuar projesi yok — \"Fuar To Do List\" ile ilkini oluştur."
              : t("projects.empty")}
          </p>
        </div>
      ) : (() => {
        // Compute the visible slice once so we can render a targeted
        // empty state ("hiç gecikmiş görev yok") when the filter
        // returns zero projects.
        const q = search.trim().toLocaleLowerCase("tr");
        const visible = projects.filter((p) => {
          if (q) {
            const hay = [
              p.name,
              p.description,
              p.todo_template ? templateLabel(p.todo_template) : null,
              p.fair_id ? fairById[p.fair_id]?.name : null,
            ]
              .filter(Boolean)
              .join(" ")
              .toLocaleLowerCase("tr");
            if (!hay.includes(q)) return false;
          }
          const baseOk = isMe
            ? (p.open_for_me_count || 0) > 0 || p.has_overdue_for_me
            : true;
          if (!baseOk) return false;
          if (filterKey === "overdue") {
            return isMe ? !!p.has_overdue_for_me : !!p.has_overdue;
          }
          if (filterKey === "open") {
            const c = isMe ? p.open_for_me_count || 0 : p.open_count || 0;
            return c > 0;
          }
          return true;
        });
        if (visible.length === 0 && (filterKey || q)) {
          return (
            <div
              data-testid="projects-filter-empty"
              className="rounded-xl border border-dashed border-zinc-300 bg-white p-16 text-center"
            >
              <p className="text-sm font-medium text-zinc-700">
                {q && "Aramanla eşleşen proje yok"}
                {!q && filterKey === "overdue" && "🎉 Gecikmiş görevin yok"}
                {!q && filterKey === "open" && "Açık görevin bulunmuyor"}
                {!q && !["overdue", "open"].includes(filterKey) && "Sonuç yok"}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                {q
                  ? "Farklı bir arama terimi dene."
                  : "Filtreyi kaldırmak için üstteki butona bas."}
              </p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((p) => {
            const isExpanded = expandedCards.has(p.id);
            const plists = projectLists[p.id];
            const cnt = isMe ? p.open_for_me_count || 0 : p.open_count || 0;
            const overdue = isMe ? p.has_overdue_for_me : p.has_overdue;
            return (
              <div
                key={p.id}
                data-testid={TEST_IDS.projectCard(p.id)}
                className="group relative block rounded-lg border border-zinc-200 bg-white p-6 transition-colors hover:border-zinc-400"
              >
                <Link to={`/app/projects/${p.id}`} className="block">
                  <div className={`mb-4 flex items-center gap-2 ${isSuperAdmin ? "pr-20" : ""}`}>
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ background: p.color || "#0055FF" }}
                    />
                    <div className="text-xs uppercase tracking-wider text-zinc-500">
                      {p.kind === "general" ? (
                        <span
                          data-testid={`project-card-general-${p.id}`}
                          className="rounded bg-teal-100 px-1.5 py-0.5 font-semibold text-teal-700"
                        >
                          Genel To Do List
                        </span>
                      ) : p.kind === "fair" ? (
                        <span
                          data-testid={`project-card-fair-kind-${p.id}`}
                          className="rounded bg-violet-100 px-1.5 py-0.5 font-semibold text-violet-700"
                        >
                          Fuar
                        </span>
                      ) : (
                        "Project"
                      )}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      {cnt > 0 && (
                        <span
                          data-testid={`project-card-badge-${p.id}`}
                          className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white"
                          title={`${cnt} tamamlanmamış görev`}
                        >
                          {cnt}
                        </span>
                      )}
                      {overdue && (
                        <span
                          data-testid={`project-card-overdue-${p.id}`}
                          title="Bu projede gecikmiş görev var"
                          className="inline-flex"
                        >
                          <Zap
                            className="h-4 w-4 fill-red-500 text-red-600"
                            strokeWidth={2}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="mb-2 font-display text-xl font-semibold text-zinc-900">
                    {p.name}
                  </h3>
                  <p className="mb-4 line-clamp-2 min-h-[2.5rem] text-sm text-zinc-500">
                    {p.description || " "}
                  </p>
                  {hasExpo && (p.todo_template || (p.fair_id && fairById[p.fair_id])) && (
                    <div
                      className="mb-3 flex flex-wrap items-center gap-1.5"
                      data-testid={`project-card-expo-${p.id}`}
                    >
                      {p.todo_template && (
                        <span
                          data-testid={`project-card-template-${p.id}`}
                          title="To Do List şablonu"
                          className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
                        >
                          <LayoutTemplate className="h-3 w-3" />
                          {templateLabel(p.todo_template)}
                        </span>
                      )}
                      {p.fair_id && fairById[p.fair_id] && (
                        <span
                          data-testid={`project-card-fair-${p.id}`}
                          title="Bağlı fuar"
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                        >
                          <CalendarDays className="h-3 w-3" />
                          {fairById[p.fair_id].name}
                        </span>
                      )}
                    </div>
                  )}
                </Link>

                {isSuperAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        requestEdit(p);
                      }}
                      data-testid={`project-card-edit-${p.id}`}
                      title="Projeyi düzenle"
                      className="absolute right-11 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 group-hover:opacity-100 focus:opacity-100"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        requestDelete(p);
                      }}
                      data-testid={`project-card-delete-${p.id}`}
                      title="Projeyi sil"
                      className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => toggleCard(p.id)}
                  data-testid={`project-card-expand-${p.id}`}
                  className="mb-2 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  Proje Grupları
                  {plists && (
                    <span className="ml-1 rounded-full bg-zinc-100 px-1.5 text-[10px] text-zinc-600">
                      {plists.length}
                    </span>
                  )}
                </button>

                {isExpanded && (
                  <div
                    data-testid={`project-card-lists-${p.id}`}
                    className="mb-3 space-y-1 border-l-2 border-zinc-100 pl-2"
                  >
                    {!plists && (
                      <p className="text-[11px] text-zinc-400">Yükleniyor...</p>
                    )}
                    {plists && plists.length === 0 && (
                      <p className="text-[11px] text-zinc-400">
                        Henüz grup yok
                      </p>
                    )}
                    {plists
                      ?.filter((l) => {
                        const baseOk = isMe
                          ? (l.open_for_me_count || 0) > 0 ||
                            l.has_overdue_for_me
                          : true;
                        if (!baseOk) return false;
                        if (filterKey === "overdue") {
                          return isMe ? !!l.has_overdue_for_me : !!l.has_overdue;
                        }
                        if (filterKey === "open") {
                          const c = isMe
                            ? l.open_for_me_count || 0
                            : l.open_count || 0;
                          return c > 0;
                        }
                        return true;
                      })
                      .map((l) => {
                      const cnt = isMe
                        ? l.open_for_me_count || 0
                        : l.open_count || 0;
                      const overdue = isMe
                        ? !!l.has_overdue_for_me
                        : !!l.has_overdue;
                      return (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() =>
                            navigate(`/app/projects/${p.id}?list=${l.id}`)
                          }
                          data-testid={`project-card-list-${l.id}`}
                          className="flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs text-zinc-700 hover:bg-zinc-50"
                        >
                          <span className="flex min-w-0 items-center gap-1.5 truncate">
                            <span className="text-zinc-400">›</span>
                            <span className="truncate">{l.name}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            {cnt > 0 && (
                              <span
                                data-testid={`project-card-list-badge-${l.id}`}
                                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white"
                                title={`${cnt} tamamlanmamış görevin`}
                              >
                                {cnt}
                              </span>
                            )}
                            {overdue && (
                              <span
                                data-testid={`project-card-list-overdue-${l.id}`}
                                title="Bu grupta gecikmiş görev var"
                                className="inline-flex"
                              >
                                <Zap
                                  className="h-4 w-4 fill-red-500 text-red-600"
                                  strokeWidth={2}
                                />
                              </span>
                            )}
                            <ArrowRight
                              className="h-3 w-3 text-zinc-400"
                              strokeWidth={1.75}
                            />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <Link
                  to={`/app/projects/${p.id}`}
                  className="flex items-center justify-between border-t border-zinc-100 pt-3 text-xs text-zinc-500 hover:text-zinc-900"
                >
                  <span>{t("projects.open")}</span>
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    strokeWidth={1.75}
                  />
                </Link>
              </div>
            );
          })}
        </div>
        );
      })()}

      {isSuperAdmin && !isFair && (
        <TrashBin
          refreshKey={trashRefreshKey}
          onRestored={() => {
            load();
          }}
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteTarget(null);
            setDeleteConfirmName("");
          }
        }}
      >
        <AlertDialogContent data-testid="projects-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Bu projeyi silmek istediğinden emin misin?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-zinc-600">
                <p>
                  <b>{deleteTarget?.name || "Bu proje"}</b> çöp kutusuna
                  taşınacak. Tüm görev, grup ve takımlar birlikte silinecek
                  ama <b>7 gün boyunca</b> çöp kutusundan geri getirebilirsin.
                </p>
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  7 gün sonra otomatik olarak kalıcı silinir.
                </p>
                <div className="pt-2">
                  <Label
                    htmlFor="projects-delete-confirm"
                    className="text-xs tracking-wider text-zinc-500"
                  >
                    Onaylamak için proje adını yaz: <b className="text-zinc-900">{deleteTarget?.name}</b>
                  </Label>
                  <Input
                    id="projects-delete-confirm"
                    autoFocus
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder={deleteTarget?.name || ""}
                    data-testid="projects-delete-confirm-input"
                    className="mt-1"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="projects-delete-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                deleting ||
                deleteConfirmName.trim().toLocaleLowerCase("tr") !==
                  (deleteTarget?.name || "").trim().toLocaleLowerCase("tr")
              }
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              data-testid="projects-delete-confirm-btn"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Siliniyor..." : "Çöpe taşı"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="projects-edit-dialog">
          <DialogHeader>
            <DialogTitle>Projeyi düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="project-edit-name">Proje adı</Label>
              <Input
                id="project-edit-name"
                autoFocus
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="projects-edit-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="project-edit-desc">Açıklama</Label>
              <Textarea
                id="project-edit-desc"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, description: e.target.value }))
                }
                data-testid="projects-edit-desc-input"
                className="mt-1"
                rows={3}
              />
            </div>
            {editTarget?.kind !== "general" && hasExpo && (
              <div>
                <Label>To Do List Şablonu</Label>
                <Select
                  value={editForm.todo_template || "__none__"}
                  onValueChange={(v) =>
                    setEditForm((f) => ({ ...f, todo_template: v === "__none__" ? "" : v }))
                  }
                >
                  <SelectTrigger className="mt-1" data-testid="projects-edit-template-select">
                    <SelectValue placeholder="Şablonsuz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Şablonsuz</SelectItem>
                    {TODO_TEMPLATES.map((tpl) => (
                      <SelectItem key={tpl.value} value={tpl.value} data-testid={`projects-edit-template-${tpl.value}`}>
                        {tpl.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editForm.todo_template !== editInitialTemplate && (
                  <p
                    data-testid="projects-edit-template-warning"
                    className="mt-1 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
                  >
                    Şablon değiştirilirse eski şablonun grupları ve içindeki görevler
                    silinir, yeni şablonun grupları oluşturulur.
                  </p>
                )}
              </div>
            )}
            {editTarget?.kind !== "general" && hasExpo && (
              <FairSelect
                value={editForm.fair_id}
                onChange={(v) => setEditForm((f) => ({ ...f, fair_id: v }))}
                testId="projects-edit-fair-select"
              />
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditTarget(null)}
                data-testid="projects-edit-cancel"
              >
                Vazgeç
              </Button>
              <Button
                disabled={savingEdit || !editForm.name.trim()}
                onClick={confirmEdit}
                data-testid="projects-edit-save"
              >
                {savingEdit ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
